import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/useMobile";
import { cn } from "@/lib/utils";
import type { CleoItemHoverExplanation } from "@shared/proposalItemCleoDescription";
import { CircleHelp } from "lucide-react";

type Props = {
  explanation: CleoItemHoverExplanation;
  className?: string;
};

function HoverBody({ explanation }: { explanation: CleoItemHoverExplanation }) {
  return (
    <div className="space-y-3 text-left text-[#1A2744]">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-[#1A2744]">What this is</p>
        <p className="mt-1 text-sm leading-relaxed text-[#1A2744]/90">{explanation.whatThisIs}</p>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-[#1A2744]">Why you need it</p>
        <p className="mt-1 text-sm leading-relaxed text-[#1A2744]/90">{explanation.whyYouNeedIt}</p>
      </div>
    </div>
  );
}

/** Info affordance for Cleo hover explanation — desktop hover, mobile tap. */
export default function ProposalItemCleoExplanation({ explanation, className }: Props) {
  const isMobile = useIsMobile();
  const what = String(explanation.whatThisIs ?? "").trim();
  const why = String(explanation.whyYouNeedIt ?? "").trim();
  if (!what || !why) return null;

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "h-7 w-7 shrink-0 rounded-full text-[#1A2744]/65 hover:bg-[#1A2744]/8 hover:text-[#1A2744]",
        className,
      )}
      aria-label="What does this do?"
    >
      <CircleHelp className="h-4 w-4" aria-hidden />
    </Button>
  );

  const body = <HoverBody explanation={{ whatThisIs: what, whyYouNeedIt: why }} />;

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          className="max-w-[min(90vw,22rem)] border border-[#1A2744]/12 bg-white p-4 text-[#1A2744] shadow-lg"
          align="start"
          side="top"
        >
          {body}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[min(90vw,26rem)] border border-[#1A2744]/12 bg-white p-3 text-left shadow-lg [&>svg]:fill-white [&>svg]:bg-white"
      >
        {body}
      </TooltipContent>
    </Tooltip>
  );
}
